import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { apiErrorMessage, launchApi } from '../api';
import { LAUNCH_FILE_EXTENSIONS } from '../fileRules';
import { formatDate } from '../helpers';
import { help } from '../helpTexts';
import { launchColors, overlineSx, pageTitleSx } from '../launchTheme';
import { Assignee, LaunchSettings } from '../types';
import SectionCard from '../ui/SectionCard';
import ChainCalendarTab from './ChainCalendarTab';
import ProcessTemplateTab from './ProcessTemplateTab';

interface Props {
  assignees: Assignee[];
  onClose: () => void;
}

type SettingsTab = 'folders' | 'chains' | 'roles' | 'files' | 'time' | 'log' | 'calendar' | 'template';

const tabNote: Record<SettingsTab, string> = {
  folders: 'Gäller lanseringar som skapas efteråt. Pågående lanseringar behåller sina mappar.',
  chains: 'Gäller lanseringar som skapas efteråt. Pågående lanseringar behåller sina kedjor.',
  roles: 'Gäller lanseringar som skapas efteråt. Pågående lanseringar behåller sina personer.',
  files: 'Gäller direkt, även i lanseringar som redan finns.',
  time: 'Gäller direkt, även i lanseringar som redan finns.',
  log: 'Visar de senaste sparade ändringarna av inställningarna.',
  calendar: 'Publicerade fönster gäller nya lanseringar. Befintliga datum behålls.',
  template: 'En publicerad mall gäller nya lanseringar. Befintliga planer behålls.',
};

const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'tif', 'tiff'];
const officeExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
const groupedExtensions = new Set([...imageExtensions, ...officeExtensions]);

const fileGroups: Array<{ title: string; extensions: string[] }> = [
  { title: 'Bilder', extensions: LAUNCH_FILE_EXTENSIONS.filter((extension) => imageExtensions.includes(extension)) },
  { title: 'Office', extensions: LAUNCH_FILE_EXTENSIONS.filter((extension) => officeExtensions.includes(extension)) },
  { title: 'Övrigt', extensions: LAUNCH_FILE_EXTENSIONS.filter((extension) => !groupedExtensions.has(extension)) },
];

function newChainId(used: string[]): string {
  const suffix = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 8)
    : String(Date.now());
  let id = `chain-${suffix}`;
  let extra = 2;
  while (used.includes(id) || id.length > 41) {
    id = `chain-${suffix}-${extra}`;
    extra += 1;
  }
  return id;
}

const namesMissing = (settings: LaunchSettings): boolean => (
  settings.fileFolders.some((name) => !name.trim()) || settings.chains.some((chain) => !chain.name.trim())
);

const LaunchSettingsView: React.FC<Props> = ({ assignees, onClose }) => {
  const [settings, setSettings] = useState<LaunchSettings | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [tab, setTab] = useState<SettingsTab>('folders');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    launchApi.settings()
      .then((next) => { if (!cancelled) setSettings(next); })
      .catch((requestError) => { if (!cancelled) setError(apiErrorMessage(requestError, 'Inställningarna kunde inte hämtas.')); });
    launchApi.templateRoles()
      .then((next) => { if (!cancelled) setRoles(next); })
      .catch(() => { if (!cancelled) setRoles([]); });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    if (!settings || namesMissing(settings)) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await launchApi.saveSettings(settings);
      setSettings(saved);
      setNotice('Inställningarna har sparats.');
    } catch (requestError: any) {
      if (requestError?.response?.status === 409) {
        const current = await launchApi.settings();
        setSettings(current);
        setError('Någon annan hann ändra inställningarna. De har laddats om.');
      } else {
        setError(apiErrorMessage(requestError, 'Inställningarna kunde inte sparas.'));
      }
    } finally {
      setBusy(false);
    }
  };

  if (!settings) {
    return (
      <Stack spacing={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={onClose}>Tillbaka</Button>
        {error ? <Alert severity="error">{error}</Alert> : <Typography>Laddar inställningar…</Typography>}
      </Stack>
    );
  }

  const savesHere = tab !== 'calendar' && tab !== 'template';
  const assignedRoles = roles.filter((role) => settings.roleDefaults[role]).length;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton aria-label="Tillbaka" onClick={onClose}><ArrowBackIcon /></IconButton>
        <Typography component="h2" sx={[pageTitleSx, { flex: 1 }]}>Inställningar</Typography>
        {savesHere && (
          <Button variant="contained" disabled={busy || namesMissing(settings)} onClick={() => { void save(); }}>Spara</Button>
        )}
      </Stack>
      <Typography sx={{ color: launchColors.textSecondary }}>{tabNote[tab]}</Typography>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
      <Tabs
        value={tab}
        onChange={(_event, value: SettingsTab) => setTab(value)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ borderBottom: `1px solid ${launchColors.divider}` }}
      >
        <Tab value="folders" label="Mappar" />
        <Tab value="chains" label="Kedjor" />
        <Tab value="roles" label="Roller" />
        <Tab value="files" label="Filregler" />
        <Tab value="time" label="Tidsgränser" />
        <Tab value="log" label="Ändringslogg" />
        <Tab value="calendar" label="Kedjekalender" />
        <Tab value="template" label="Processmall" />
      </Tabs>
      {tab === 'folders' && (
        <SectionCard title="Mappar" info={help.settingsFolders} headingLevel="h3">
          <Stack spacing={1} alignItems="flex-start">
            {settings.fileFolders.map((name, index) => (
              <Stack key={`folder-${index}`} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ width: '100%', py: 0.5, borderBottom: `1px solid ${launchColors.rowDivider}` }}>
                <TextField
                  fullWidth
                  label={`Mapp ${index + 1}`}
                  value={name}
                  error={!name.trim()}
                  helperText={name.trim() ? undefined : 'Ange ett namn.'}
                  onChange={(event) => setSettings({ ...settings, fileFolders: settings.fileFolders.map((item, itemIndex) => itemIndex === index ? event.target.value : item) })}
                />
                <IconButton
                  aria-label={`Ta bort mapp ${index + 1}`}
                  disabled={settings.fileFolders.length <= 1}
                  onClick={() => setSettings({ ...settings, fileFolders: settings.fileFolders.filter((_item, itemIndex) => itemIndex !== index) })}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            ))}
            <Button startIcon={<AddIcon />} onClick={() => setSettings({ ...settings, fileFolders: [...settings.fileFolders, ''] })}>Lägg till mapp</Button>
          </Stack>
        </SectionCard>
      )}
      {tab === 'chains' && (
        <SectionCard title="Kedjor" info={help.settingsChains} headingLevel="h3">
          <Stack spacing={1} alignItems="flex-start">
            {settings.chains.map((chain) => (
              <Stack key={chain.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ width: '100%', py: 0.5, borderBottom: `1px solid ${launchColors.rowDivider}` }}>
                <TextField
                  fullWidth
                  label="Kedja"
                  value={chain.name}
                  error={!chain.name.trim()}
                  helperText={chain.name.trim() ? undefined : 'Ange ett namn.'}
                  onChange={(event) => setSettings({ ...settings, chains: settings.chains.map((item) => item.id === chain.id ? { ...item, name: event.target.value } : item) })}
                />
                <FormControlLabel
                  sx={{ mr: 0, whiteSpace: 'nowrap' }}
                  control={<Switch checked={chain.active} onChange={(event) => setSettings({ ...settings, chains: settings.chains.map((item) => item.id === chain.id ? { ...item, active: event.target.checked } : item) })} />}
                  label="Aktiv"
                />
                <IconButton
                  aria-label={`Ta bort ${chain.name}`}
                  disabled={settings.chains.length <= 1}
                  onClick={() => setSettings({ ...settings, chains: settings.chains.filter((item) => item.id !== chain.id) })}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            ))}
            <Button startIcon={<AddIcon />} onClick={() => setSettings({ ...settings, chains: [...settings.chains, { id: newChainId(settings.chains.map((chain) => chain.id)), name: '', active: true }] })}>Lägg till kedja</Button>
          </Stack>
        </SectionCard>
      )}
      {tab === 'roles' && (
        <SectionCard title="Roller" info={help.settingsRoles} headingLevel="h3" subtitle={`${assignedRoles} av ${roles.length} roller har en förvald person`}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {roles.map((role) => (
              <TextField
                key={role}
                select
                label={role}
                value={settings.roleDefaults[role] || ''}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => {
                  const roleDefaults = { ...settings.roleDefaults };
                  if (event.target.value) roleDefaults[role] = event.target.value;
                  else delete roleDefaults[role];
                  setSettings({ ...settings, roleDefaults });
                }}
              >
                <MenuItem value="">Ingen</MenuItem>
                {settings.roleDefaults[role] && !assignees.some((person) => person.id === settings.roleDefaults[role]) && (
                  <MenuItem value={settings.roleDefaults[role]}>{settings.roleDefaults[role]}</MenuItem>
                )}
                {assignees.map((person) => <MenuItem key={person.id} value={person.id}>{person.name}</MenuItem>)}
              </TextField>
            ))}
          </Box>
        </SectionCard>
      )}
      {tab === 'files' && (
        <SectionCard title="Filregler" info={help.settingsFiles} headingLevel="h3">
          <Stack spacing={2}>
            <TextField
              type="number"
              label="Maxstorlek i MB"
              value={settings.fileRules.maxFileMb}
              helperText="1–100 MB"
              inputProps={{ min: 1, max: 100 }}
              sx={{ maxWidth: 220 }}
              onChange={(event) => setSettings({ ...settings, fileRules: { ...settings.fileRules, maxFileMb: Number(event.target.value) } })}
            />
            {fileGroups.map((group) => (
              <Stack key={group.title} spacing={0.5}>
                <Typography sx={overlineSx}>{group.title}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 0.5 }}>
                  {group.extensions.map((extension) => (
                    <FormControlLabel
                      key={extension}
                      control={<Checkbox checked={settings.fileRules.allowedExtensions.includes(extension)} onChange={(event) => {
                        const allowedExtensions = event.target.checked
                          ? [...settings.fileRules.allowedExtensions, extension]
                          : settings.fileRules.allowedExtensions.filter((item) => item !== extension);
                        setSettings({ ...settings, fileRules: { ...settings.fileRules, allowedExtensions } });
                      }} />}
                      label={extension}
                    />
                  ))}
                </Box>
              </Stack>
            ))}
          </Stack>
        </SectionCard>
      )}
      {tab === 'time' && (
        <SectionCard title="Tidsgränser" info={help.settingsTime} headingLevel="h3">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              type="number"
              label="Snart"
              value={settings.thresholds.dueSoonDays}
              helperText="Dagar före deadline. 1–30, standard 7."
              inputProps={{ min: 1, max: 30 }}
              onChange={(event) => setSettings({ ...settings, thresholds: { ...settings.thresholds, dueSoonDays: Number(event.target.value) } })}
            />
          </Stack>
        </SectionCard>
      )}
      {tab === 'log' && (
        <SectionCard title="Ändringslogg" info={help.settingsLog} headingLevel="h3">
          <Stack spacing={1}>
            {settings.changeLog.length === 0 && <Typography sx={{ color: launchColors.textSecondary }}>Inga ändringar är sparade ännu.</Typography>}
            {settings.changeLog.slice().reverse().map((change) => (
              <Stack key={`${change.at}-${change.section}`} spacing={0.25} sx={{ py: 1, borderBottom: `1px solid ${launchColors.rowDivider}` }}>
                <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>{formatDate(change.at)}</Typography>
                <Typography sx={overlineSx}>{change.section}</Typography>
                <Typography>{change.summary}</Typography>
              </Stack>
            ))}
          </Stack>
        </SectionCard>
      )}
      {tab === 'calendar' && <ChainCalendarTab />}
      {tab === 'template' && <ProcessTemplateTab />}
    </Stack>
  );
};

export default LaunchSettingsView;
